import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTitle } from '@/lib/useTitle'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const DEFAULT_SETTINGS: Record<string, string> = {
  company_name: 'Velink SAS',
  company_address: '',
  company_email: 'contact@velink.fr',
}

function useSettings() {
  const [settings, setSettings] = useState<Record<string, string>>(DEFAULT_SETTINGS)

  useEffect(() => {
    fetch(`${API}/api/public/settings`)
      .then((res) => {
        if (!res.ok) throw new Error()
        return res.json()
      })
      .then((data) => setSettings((prev) => ({ ...prev, ...data })))
      .catch(() => {})
  }, [])

  return settings
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-text-primary mb-4 pb-2 border-b border-white/[0.08]">
        {title}
      </h2>
      <div className="space-y-3 text-text-secondary text-sm leading-relaxed">
        {children}
      </div>
    </section>
  )
}

function P({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="list-disc list-inside space-y-1 pl-2">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  )
}

const TOC = [
  { id: 'responsable',    label: '1. Responsable du traitement' },
  { id: 'donnees',        label: '2. Données collectées' },
  { id: 'finalites',      label: '3. Finalités du traitement' },
  { id: 'base-legale',    label: '4. Base légale' },
  { id: 'destinataires',  label: '5. Destinataires des données' },
  { id: 'transferts',     label: '6. Transferts hors UE' },
  { id: 'conservation',   label: '7. Durée de conservation' },
  { id: 'securite',       label: '8. Sécurité des données' },
  { id: 'cookies',        label: '9. Cookies et traceurs' },
  { id: 'droits',         label: '10. Vos droits' },
  { id: 'exercice',       label: '11. Exercer vos droits' },
  { id: 'mineurs',        label: '12. Mineurs' },
  { id: 'modifications',  label: '13. Modifications' },
]

export default function ConfidentialitePage() {
  useTitle('Politique de confidentialité')
  const s = useSettings()

  const COMPANY_NAME    = s.company_name    || 'Velink SAS'
  const COMPANY_ADDRESS = s.company_address || '[Adresse du siège social]'
  const COMPANY_EMAIL   = s.company_email   || 'contact@velink.fr'
  const LAST_UPDATE = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen">
      <header className="glass-navbar fixed top-0 inset-x-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center">
              <span className="text-accent font-bold text-sm">V</span>
            </div>
            <span className="font-semibold text-text-primary text-lg tracking-tight">Velink</span>
          </Link>
          <Link to="/" className="glass-btn px-4 py-2 text-sm">
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Politique de confidentialité
          </h1>
          <p className="text-text-muted text-sm">
            Dernière mise à jour : {LAST_UPDATE}
          </p>
          <div className="mt-4 px-4 py-3 rounded-xl bg-accent/10 border border-accent/20 text-[#8B9AFF] text-sm">
            Cette politique explique quelles données personnelles Velink collecte, pourquoi, et comment
            vous pouvez exercer vos droits, conformément au RGPD (Règlement UE 2016/679).
          </div>
        </div>

        <div className="flex gap-10 items-start">
          <nav className="hidden lg:block w-64 flex-shrink-0 sticky top-24 glass-card p-5 rounded-2xl">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Sommaire</p>
            <ul className="space-y-2">
              {TOC.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-xs text-text-secondary hover:text-text-primary transition-colors block leading-snug"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex-1 space-y-10">

            <Section id="responsable" title="1. Responsable du traitement">
              <P>
                Le responsable du traitement des données personnelles collectées sur la plateforme Velink est{' '}
                <strong className="text-text-primary">{COMPANY_NAME}</strong>, dont le siège social est situé{' '}
                {COMPANY_ADDRESS}.
              </P>
              <P>
                Pour toute question relative à vos données personnelles, vous pouvez nous contacter à l'adresse :{' '}
                <a href={`mailto:${COMPANY_EMAIL}`} className="text-accent hover:underline">{COMPANY_EMAIL}</a>.
              </P>
            </Section>

            <Section id="donnees" title="2. Données que nous collectons">
              <P>Selon votre profil (entreprise, centre de formation ou prestataire), nous collectons :</P>
              <Ul items={[
                <><strong className="text-text-primary">Données d'identité et de contact</strong> : nom, prénom ou raison sociale, email, téléphone, adresse.</>,
                <><strong className="text-text-primary">Données professionnelles</strong> : SIRET, spécialités, qualifications, documents justificatifs (Kbis, assurance RC Pro, RIB), avis et notes.</>,
                <><strong className="text-text-primary">Données de géolocalisation</strong> : ville et coordonnées approximatives, utilisées pour le matching entre missions et prestataires à proximité.</>,
                <><strong className="text-text-primary">Données de paiement</strong> : traitées directement par nos prestataires de paiement (Stripe, PayPal) — Velink ne stocke aucune donnée bancaire (numéro de carte, IBAN complet).</>,
                <><strong className="text-text-primary">Données de connexion et d'usage</strong> : historique des missions, messages échangés dans le cadre d'une mission, journaux techniques (adresse IP, horodatage, type d'appareil).</>,
              ]} />
            </Section>

            <Section id="finalites" title="3. Pourquoi nous utilisons ces données">
              <Ul items={[
                'Créer et gérer votre compte, et vous authentifier de façon sécurisée.',
                'Mettre en relation les entreprises et les prestataires qualifiés à proximité (matching géographique).',
                'Vous envoyer des notifications liées à vos missions (push, email).',
                'Traiter les paiements et prévenir la fraude, via nos prestataires de paiement.',
                'Établir la confiance sur la plateforme (système de notation).',
                'Répondre à nos obligations légales, comptables et fiscales.',
                'Améliorer la plateforme et mesurer son usage, de façon agrégée et anonymisée.',
              ]} />
            </Section>

            <Section id="base-legale" title="4. Base légale du traitement">
              <Ul items={[
                <><strong className="text-text-primary">Exécution du contrat</strong> : la majorité des traitements (création de compte, mise en relation, paiement) sont nécessaires à la fourniture du service que vous nous demandez.</>,
                <><strong className="text-text-primary">Intérêt légitime</strong> : amélioration du service, prévention de la fraude, sécurité de la plateforme.</>,
                <><strong className="text-text-primary">Obligation légale</strong> : conservation de certaines données à des fins comptables et fiscales.</>,
                <><strong className="text-text-primary">Consentement</strong> : lorsque nous vous le demandons explicitement (ex. certaines communications marketing, si applicable).</>,
              ]} />
            </Section>

            <Section id="destinataires" title="5. À qui vos données sont-elles transmises ?">
              <P>Vos données ne sont jamais vendues. Elles peuvent être partagées avec :</P>
              <Ul items={[
                'Les autres utilisateurs de la plateforme, dans la stricte mesure nécessaire à la réalisation d\'une mission (ex. une entreprise voit le nom et le profil du prestataire qu\'elle a accepté).',
                <><strong className="text-text-primary">Stripe</strong> et <strong className="text-text-primary">PayPal</strong>, pour le traitement des paiements.</>,
                <><strong className="text-text-primary">Firebase Cloud Messaging</strong> (Google), pour l'envoi des notifications push.</>,
                <><strong className="text-text-primary">Resend / Brevo</strong>, pour l'envoi des emails transactionnels.</>,
                <><strong className="text-text-primary">Hostinger</strong>, notre hébergeur, pour le stockage des données sur ses serveurs situés dans l'Union européenne.</>,
                'Les autorités compétentes, si la loi nous y oblige.',
              ]} />
            </Section>

            <Section id="transferts" title="6. Transferts de données hors Union européenne">
              <P>
                Nos serveurs applicatifs et notre base de données sont hébergés en Europe. Certains de nos
                prestataires (notamment Google pour Firebase Cloud Messaging) peuvent traiter des données en
                dehors de l'Union européenne. Dans ce cas, ce transfert est encadré par des clauses
                contractuelles types approuvées par la Commission européenne, garantissant un niveau de
                protection équivalent au RGPD.
              </P>
            </Section>

            <Section id="conservation" title="7. Combien de temps conservons-nous vos données ?">
              <Ul items={[
                'Données de compte actif : pendant toute la durée d\'utilisation de la plateforme.',
                'Après suppression du compte : les données sont supprimées sous 30 jours, sauf celles que nous devons conserver plus longtemps pour des raisons légales (documents comptables : 10 ans, conformément au Code de commerce).',
                'Données de connexion et journaux techniques : 12 mois maximum.',
              ]} />
            </Section>

            <Section id="securite" title="8. Comment protégeons-nous vos données ?">
              <P>
                Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :
                chiffrement des mots de passe, connexions HTTPS, accès restreint aux données par notre équipe,
                hébergement chez un prestataire certifié. Aucune méthode de transmission ou de stockage n'étant
                sûre à 100 %, nous ne pouvons toutefois garantir une sécurité absolue.
              </P>
            </Section>

            <Section id="cookies" title="9. Cookies et traceurs">
              <P>
                Velink utilise uniquement des cookies techniques strictement nécessaires au fonctionnement du
                site (maintien de votre session de connexion, préférences). Ces cookies ne nécessitent pas de
                consentement préalable au sens de la réglementation ePrivacy, car ils sont indispensables au
                service demandé.
              </P>
              <P>
                Aucun cookie publicitaire ou de traçage à des fins commerciales n'est déposé sans votre
                consentement explicite.
              </P>
            </Section>

            <Section id="droits" title="10. Vos droits">
              <P>Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :</P>
              <Ul items={[
                <><strong className="text-text-primary">Droit d'accès</strong> : obtenir une copie des données que nous détenons sur vous.</>,
                <><strong className="text-text-primary">Droit de rectification</strong> : corriger des données inexactes ou incomplètes.</>,
                <><strong className="text-text-primary">Droit à l'effacement</strong> (« droit à l'oubli ») : demander la suppression de vos données, sous réserve de nos obligations légales de conservation.</>,
                <><strong className="text-text-primary">Droit à la portabilité</strong> : recevoir vos données dans un format structuré et lisible par machine.</>,
                <><strong className="text-text-primary">Droit d'opposition</strong> : vous opposer à un traitement fondé sur notre intérêt légitime.</>,
                <><strong className="text-text-primary">Droit à la limitation</strong> : demander la suspension temporaire d'un traitement.</>,
                <><strong className="text-text-primary">Droit de retirer votre consentement</strong> à tout moment, lorsque le traitement en dépend.</>,
              ]} />
            </Section>

            <Section id="exercice" title="11. Comment exercer vos droits">
              <P>
                Pour exercer l'un de ces droits, contactez-nous à{' '}
                <a href={`mailto:${COMPANY_EMAIL}`} className="text-accent hover:underline">{COMPANY_EMAIL}</a>{' '}
                en précisant votre demande et en joignant un justificatif d'identité si nécessaire. Nous nous
                engageons à vous répondre dans un délai maximum d'un mois.
              </P>
              <P>
                Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez
                introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des
                Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">www.cnil.fr</a>.
              </P>
            </Section>

            <Section id="mineurs" title="12. Mineurs">
              <P>
                La plateforme Velink est réservée aux personnes majeures (18 ans ou plus) ou aux personnes
                morales régulièrement immatriculées. Nous ne collectons pas sciemment de données concernant des
                mineurs.
              </P>
            </Section>

            <Section id="modifications" title="13. Modifications de cette politique">
              <P>
                Nous pouvons modifier cette politique de confidentialité pour refléter des évolutions légales,
                techniques ou de nos pratiques. Toute modification substantielle vous sera communiquée par email
                ou notification in-app avant son entrée en vigueur.
              </P>
            </Section>

            <p className="text-center text-text-muted text-xs pt-4">
              Voir aussi : <Link to="/mentions-legales" className="text-accent hover:underline">Mentions légales</Link>
              {' · '}
              <Link to="/cgv" className="text-accent hover:underline">Conditions Générales de Vente</Link>
            </p>

          </div>
        </div>
      </main>
    </div>
  )
}
